import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Container,
  Paper,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert
} from '@mui/material';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import { auth, db } from '../../firebase-config';
import { useAuth } from '../../context/AuthContext';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';

// Import step components
import BusinessInfoStep from '../../components/BusinessInfoStep';
import PhoneSetupStep from '../../components/PhoneSetupStep';
import IntroMessageStep from '../../components/IntroMessageStep';
import QAStep from '../../components/QAStep';
import ReviewSubmitStep from '../../components/ReviewSubmitStep';
import TwilioNumberStep from './TwilioNumberStep';
import PaymentStatusUpdater from '../../components/PaymentStatusUpdater';

// Define steps
const steps = ['Phone Setup', 'Business Information', 'Business Line', 'Intro Message', 'Q&A Training', 'Review & Submit'];

export default function OnboardingFlow() {
  console.log('Rendering full OnboardingFlow component');

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    phoneData: {
      phoneNumber: '',
      carrier: '',
      carrierDetails: null
    },
    businessInfo: {
      name: '',
      description: ''
    },
    twilioNumber: '',
    introMessage: "", // Will be set dynamically in IntroMessageStep based on business name
    commonQuestions: [
      {
        question: 'What are your business hours?',
        answer: 'Monday - Saturday 7-5 PM.'
      },
      {
        question: 'How do I make an appointment?',
        answer: 'I will give you a call shortly to confirm a time and date that works for you. Thank you.'
      }
    ]
  });

  // Check if user has already completed onboarding and get business ID if available
  const [businessId, setBusinessId] = useState(null);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!currentUser) return;

      try {
        // First check the user document for a businessId from payment flow
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);

        // Check if payment is complete in localStorage
        const localPaymentComplete = localStorage.getItem('paymentComplete');
        const stripeSessionId = localStorage.getItem('stripeSessionId');

        console.log('OnboardingFlow: Local storage payment status:', localPaymentComplete);
        console.log('OnboardingFlow: Local storage session ID:', stripeSessionId);

        // If we have payment info in localStorage but not in user document, update it
        if ((localPaymentComplete === 'true' || stripeSessionId) &&
            userSnap.exists() && !userSnap.data().paymentComplete) {
          console.log('OnboardingFlow: Setting payment status in user document');
          await updateDoc(userRef, {
            paymentComplete: true,
            ...(stripeSessionId ? { stripeSessionId } : {}),
            updatedAt: new Date()
          });

          // Also update business document
          const businessRef = doc(db, 'businesses', currentUser.uid);
          const businessSnap = await getDoc(businessRef);

          if (businessSnap.exists() && !businessSnap.data().paymentComplete) {
            console.log('OnboardingFlow: Setting payment status in business document');
            await updateDoc(businessRef, {
              paymentComplete: true,
              ...(stripeSessionId ? { stripeSessionId } : {}),
              updatedAt: new Date()
            });
          }
        }

        // Fire Google Ads conversion event for completed payments
        const hasPaymentComplete = userSnap.exists() &&
          (userSnap.data().paymentComplete || localPaymentComplete === 'true');

        if (hasPaymentComplete && !localStorage.getItem('conversionEventFired')) {
          console.log('OnboardingFlow: Firing Google Ads conversion event');

          // Get transaction ID from Stripe session or generate one
          const transactionId = stripeSessionId ||
            localStorage.getItem('stripeSessionId') ||
            `conversion_${currentUser.uid}_${Date.now()}`;

          // Fire the conversion event
          if (window.gtag) {
            window.gtag('event', 'conversion', {
              'send_to': 'AW-11194998288/unM7CIC50M4aEJC8mNop',
              'transaction_id': transactionId
            });

            console.log('Google Ads conversion event fired with transaction ID:', transactionId);

            // Mark that we've fired the conversion event to prevent duplicates
            localStorage.setItem('conversionEventFired', 'true');
            localStorage.setItem('conversionEventTimestamp', Date.now().toString());
          } else {
            console.warn('Google Analytics gtag not available for conversion tracking');
          }
        }

        if (userSnap.exists()) {
          const userData = userSnap.data();

          // If user has a businessId from payment flow, use that
          if (userData.businessId) {
            console.log('Found businessId in user profile:', userData.businessId);
            setBusinessId(userData.businessId);

            // Check if this business has onboarding completed
            const businessRef = doc(db, 'businesses', userData.businessId);
            const businessSnap = await getDoc(businessRef);

            if (businessSnap.exists() && businessSnap.data().onboardingComplete) {
              console.log('Business already completed onboarding, redirecting to dashboard');
              navigate('/dashboard');
              return;
            }
          }
        }

        // Fallback to checking if there's a business with the user's ID
        const businessRef = doc(db, 'businesses', currentUser.uid);
        const businessSnap = await getDoc(businessRef);

        if (businessSnap.exists() && businessSnap.data().onboardingComplete) {
          console.log('User already completed onboarding, redirecting to dashboard');
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Error checking onboarding status:', err);
      }
    };

    checkOnboardingStatus();
  }, [currentUser, navigate, db]);

  // Handle user logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Component mounting effect
  useEffect(() => {
    console.log('Multi-step OnboardingFlow mounted');
    document.title = 'Onboarding - Missed Call MVP';

    return () => {
      console.log('Multi-step OnboardingFlow unmounted');
    };
  }, []);

  // Handle form data updates
  const handleFormUpdate = (data) => {
    console.log('Form data updated:', data);
    setFormData(prev => ({
      ...prev,
      ...data
    }));
  };

  // Handle next step
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle completion
  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Determine which business ID to use
      const targetBusinessId = businessId || currentUser.uid;
      console.log('Using business ID for onboarding completion:', targetBusinessId);

      // Create the document references
      const businessRef = doc(db, 'businesses', targetBusinessId);
      const userRef = doc(db, 'users', currentUser.uid);

      // Structure the data to match dashboard expectations
      const businessData = {
        businessInfo: formData.businessInfo,
        phoneData: formData.phoneData,
        twilioNumber: formData.twilioNumber,
        introMessage: formData.introMessage,
        commonQuestions: formData.commonQuestions,
        onboardingComplete: true,
        updatedAt: new Date().toISOString(),
        userId: currentUser.uid
      };

      // If this is a new business (not from payment flow), add createdAt
      if (!businessId) {
        businessData.createdAt = new Date().toISOString();
      }

      console.log('Saving business data:', JSON.stringify(businessData, null, 2));

      // Save all data to Firestore
      await setDoc(businessRef, businessData, { merge: true });

      // Also mark onboarding complete in the user document and ensure businessId is set
      await setDoc(userRef, {
        onboardingComplete: true,
        updatedAt: new Date().toISOString(),
        email: currentUser.email,
        phoneNumber: formData.phoneData.phoneNumber,
        businessId: targetBusinessId
      }, { merge: true });

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render current step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <PhoneSetupStep
            data={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onBack={() => {}} // No back on first step
          />
        );
      case 1:
        return (
          <BusinessInfoStep
            data={formData.businessInfo}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <TwilioNumberStep
            businessInfo={formData.businessInfo}
            onNext={(data) => {
              handleFormUpdate({ twilioNumber: data.twilioNumber });
              handleNext();
            }}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <IntroMessageStep
            data={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <QAStep
            data={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 5:
        return (
          <ReviewSubmitStep
            data={formData}
            onUpdate={handleFormUpdate}
            onComplete={handleComplete}
            onBack={handleBack}
          />
        );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  if (!currentUser) {
    console.log('No user in onboarding flow');
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h4">Please log in</Typography>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Silent component to ensure payment status is set */}
      <PaymentStatusUpdater />

      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Onboarding
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, flex: 1 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Box sx={{ mt: 2 }}>
            {getStepContent(activeStep)}
          </Box>

          {/* Bottom Navigation - Only show if not handled by step components */}
          {false && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
              >
                Back
              </Button>

              <Button
                variant="contained"
                disabled={loading}
                onClick={activeStep === steps.length - 1 ? handleComplete : handleNext}
              >
                {loading ? <CircularProgress size={24} /> :
                  activeStep === steps.length - 1 ? 'Complete' : 'Next'}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}